import React, { useState, forwardRef } from "react";

import { Icon, Button } from "kit/ui";

import FormField from "./Form.Field";
import FormLabel from "./Form.Label";
import FormHelp from "./Form.Help";
import FormWrapper from "./Form.Wrapper";
import FormValidation from "./Form.Validation";

import * as styles from "./InputField.styles";

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  help?: React.ReactNode;
  floating?: boolean;
  inputLeft?: React.ReactNode;
  inputRight?: React.ReactNode;
  fieldProps?: Record<string, any>;
  isInvalid?: boolean;
  isValid?: boolean;
  errorMessage?: string;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
}

const Input = forwardRef<HTMLInputElement, InputProps>(({
  id,
  type = "text",
  name,
  value,
  defaultValue,
  placeholder,
  label,
  help,
  floating = true,
  inputLeft,
  inputRight,
  fieldProps = {},
  isInvalid = false,
  isValid = false,
  errorMessage,
  onFocus,
  onBlur,
  onChange,
  ...inputProps
}, ref) => {

  const [ isHidden, setIsHidden ] = useState(true);
  const [ isFocused, setIsFocused ] = useState(false);
  const [ hasUncontrolledContent, setHasUncontrolledContent ] = useState(!!defaultValue);

  const isControlled = value !== undefined;

  const toggleShowPassword = () => setIsHidden(val => !val);

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    onFocus?.(event);
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    onBlur?.(event);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!isControlled) {
      setHasUncontrolledContent(event.target.value.length > 0);
    }
    onChange?.(event);
  };

  const fieldId = id || `input-${name || "default"}`;
  const hasValueOrFocused = isControlled ?
    (!!value || isFocused) :
    (hasUncontrolledContent || isFocused);
  const isPhone = type === "tel";
  const isPassword = type === "password";

  return (
    <FormField
      className="field-input"
      {...fieldProps}>

      <FormWrapper
        floating={floating}
        isInvalid={isInvalid}
        isValid={isValid}
        hasValueOrFocused={hasValueOrFocused}
        hasFocused={isFocused}
        inputLeft={inputLeft}
        inputRight={inputRight}>

        {
          label && (
            <FormLabel htmlFor={fieldId}>
              {label}
            </FormLabel>
          )
        }

        <div
          css={[
            styles.base,
            isFocused && styles.hasFocused,
            isInvalid && styles.error,
            isValid && styles.success
          ]}>

          {
            isPhone && (
              <span css={[
                styles.phoneFormatLabel,
                isFocused && styles.hasFocused,
                isInvalid && styles.error,
                isValid && styles.success
              ]}>
                {(!floating || hasValueOrFocused) && "+34"}
              </span>
            )
          }

          <input
            ref={ref}
            id={fieldId}
            name={name}
            {...(isControlled ? { value } : { defaultValue })}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onChange={handleChange}
            css={[
              styles.input,
              isPhone && styles.phoneInput
            ]}
            {...inputProps}
            placeholder={(floating && !hasValueOrFocused) ? "" : placeholder}
            type={isPassword ? (isHidden ? "password" : "text") : type} />

          {
            isPassword && (
              <Button
                as="button"
                unstyled
                style={{
                  margin: "0 10px 0 5px"
                }}
                tabIndex={-1}
                type="button"
                onClick={toggleShowPassword}
                aria-label={isHidden ? "Show password" : "Hide password"}
                icon={<Icon size="24" name={`eye${isHidden ? "" : "-slash"}`} />} />
            )
          }
        </div>

      </FormWrapper>

      {
        help && (
          <FormHelp>
            {help}
          </FormHelp>
        )
      }

      <FormValidation
        errorMessage={errorMessage || ""}
        isVisible={isInvalid} />

    </FormField>
  );
});

Input.displayName = "Input";

export default Input;
